import imageCompression from "browser-image-compression";
import api, { API_ORIGIN } from "../axios";

export const LINK_IMAGE_ASPECT = 1; // tiles are square
export const LINK_IMAGE_OUTPUT_WIDTH = 1024;
export const LINK_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
const LINK_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Returns an error message, or null when the file can be opened in the cropper (SVG is rejected).
// There is no size limit here: the cropped result is compressed to fit instead.
export function validateLinkImage(file) {
  if (!file || !LINK_IMAGE_TYPES.includes(file.type)) {
    return "Please choose a JPG, PNG or WebP image";
  }
  return null;
}

// Shrinks the cropped file to the 2 MB upload limit (a no-op when it already fits).
export async function compressLinkImage(file) {
  if (file.size <= LINK_IMAGE_MAX_BYTES * 0.9) return file;
  const out = await imageCompression(file, {
    maxSizeMB: (LINK_IMAGE_MAX_BYTES * 0.9) / (1024 * 1024),
    maxWidthOrHeight: LINK_IMAGE_OUTPUT_WIDTH,
    useWebWorker: true,
    fileType: "image/jpeg",
  });
  return new File([out], file.name, { type: "image/jpeg" });
}

// Resolves to { image_path, image_url }. No Content-Type set: the browser adds the multipart boundary.
export const uploadLinkImage = (file) => {
  const fd = new FormData();
  fd.append("image", file);
  return api.post("/user/link/image", fd);
};

// Validation errors arrive as 422 { msg: { field: [messages] } }.
export const linkImageErrorMessage = (err) => {
  const data = err?.response?.data;
  const first = data?.msg && Object.values(data.msg).flat()[0];
  return (
    first ||
    (typeof data?.msg === "string" ? data.msg : null) ||
    data?.message ||
    (err?.response ? "Something went wrong" : "Network error, please try again")
  );
};

// Image links have no icon, so they can't share the social href helper.
export const imageLinkHref = (link = "") =>
  /^(https?:)?\/\//i.test(link) ? link : "http://" + link;

// Routes a click through the backend so it's counted, then the server redirects to the real target.
export const trackedLinkHref = (item) =>
  item?.id != null ? `${API_ORIGIN}/r/${item.id}` : imageLinkHref(item?.link);

// Rewrites image_url to always use API_ORIGIN, in case the backend embedded a different host.
export const resolveImageUrl = (url) => {
  if (!url) return url;
  try {
    const { pathname, search } = new URL(url);
    return `${API_ORIGIN}${pathname}${search}`;
  } catch {
    return url;
  }
};

// Splits links into render blocks, keeping the user's order: runs of consecutive image links
// become one grid block, every other link is its own block.
export function groupLinks(links) {
  const blocks = [];
  links.forEach((item, index) => {
    const entry = { item, index };
    const last = blocks[blocks.length - 1];
    if (item.type === "image") {
      if (last?.kind === "images") last.entries.push(entry);
      else blocks.push({ kind: "images", entries: [entry] });
    } else {
      blocks.push({ kind: "link", ...entry });
    }
  });
  return blocks;
}
